import { SettingsAdjust } from '@carbon/react/icons';
import {
  Button,
  InlineNotification,
  NumberInput,
  OverflowMenu,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { parseLocalDateTime } from 'utils/dates';
import { formatMutationResource } from 'utils/misc';
import { wellStickLateralFactorMutationAtom } from './atoms';
import {
  DEFAULT_STICK_LATERAL_FACTOR,
  formatStickLateralFactor,
  isValidStickLateralPercent,
  roundStickLateralPercent,
  stickLateralFactorToPercent,
  stickLateralPercentToFactor,
} from './utils';
import styles from './factor-popup.module.scss';

interface StickLateralFactorPopupProps {
  uwi: string;
  value: number;
  onChange: (value: number) => void;
  onApplySuccess?: (timestamp: string) => void;
  onApplyError?: (message: string) => void;
}

// Editor for wells.stick_estimated_lateral_factor: the fraction of a horizontal well's stick (or
// its permit's borestick) valuation takes as its lateral when nothing better measures it. Edited
// and shown as a percent in (0, 100] to one decimal, like the sibling PerforationAdjustmentPopup
// (a fixed-choice list because its factor is); `value` in and `onChange` out stay the stored
// fraction in (0, 1], converted here. The draft is sent rounded to one decimal of percent (three
// of the fraction), the precision the server accepts, so a finer typed value never meets its error.
export const StickLateralFactorPopup = ({
  uwi,
  value,
  onChange,
  onApplySuccess,
  onApplyError,
}: StickLateralFactorPopupProps) => {
  const [stickFactorData, updateStickFactor] = useAtom(
    wellStickLateralFactorMutationAtom
  );
  const { loading } = formatMutationResource(stickFactorData);
  // The draft is the typed PERCENT (null while the input is empty); `value` is the stored
  // fraction, always present (the column is NOT NULL).
  const [draft, setDraft] = useState<number | null>(() =>
    stickLateralFactorToPercent(value)
  );

  // Re-seed the draft when the well's factor arrives or is applied.
  useEffect(() => {
    setDraft(stickLateralFactorToPercent(value));
  }, [value]);

  const isDraftValid = isValidStickLateralPercent(draft);
  // Compared as the fraction that would be sent, so 92.54 against a stored 0.925 is unchanged.
  const draftFactor = isDraftValid ? stickLateralPercentToFactor(draft) : null;
  // An emptied or invalid draft has no fraction, so it reads as modified (and invalid).
  const isModified = draftFactor !== value;

  const handleApply = () => {
    if (draftFactor === null) return;

    const appliedFactor = draftFactor;
    updateStickFactor({
      url: `/wells/${uwi}`,
      type: 'PATCH',
      data: { stickEstimatedLateralFactor: appliedFactor },
      onSuccess: (data: { updatedAt?: string }) => {
        onChange(appliedFactor);
        const timestamp = parseLocalDateTime(
          data?.updatedAt ?? new Date().toISOString(),
          true
        );
        onApplySuccess?.(timestamp);
      },
      onError: (error?: unknown) => {
        const errorData = error as { errors?: Array<{ message: string }> };
        onApplyError?.(
          errorData?.errors?.[0]?.message ??
            'The Stick Estimated Lateral Factor failed updating.'
        );
      },
    });
  };

  const triggerLabel = `Stick Factor ${formatStickLateralFactor(value)}`;

  return (
    <div>
      <div className={styles.menuWrapper}>
        <div className={styles.triggerContainer}>
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          <OverflowMenu
            renderIcon={SettingsAdjust}
            size="lg"
            flipped
            className={styles.overflowMenu}
            menuOptionsClass={styles.overflowMenuOptions}
            iconDescription="Stick factor settings"
            aria-label={triggerLabel}
          >
            <div className={styles.menu} data-stick-factor-menu="true">
              <h4 className={styles.menuTitle}>
                Stick Estimated Lateral Factor
              </h4>

              <p className={styles.menuDescription}>
                When a horizontal well has no survey and no pre-drill plan
                (mostly expired permits), valuation measures its stick instead:
                the permit&apos;s planned borestick, else the well&apos;s
                surface-to-bottom-hole line. A stick includes the offset from
                the surface hole to the heel, so it overstates the lateral; this
                factor scales it back.
              </p>

              <NumberInput
                id={`stick-lateral-factor-${uwi}`}
                label="Factor % (greater than 0, up to 100)"
                min={0}
                max={100}
                step={0.1}
                allowEmpty
                value={draft ?? ''}
                invalid={isModified && !isDraftValid}
                invalidText="Enter a percent greater than 0 and at most 100."
                onBlur={() => {
                  if (isDraftValid) setDraft(roundStickLateralPercent(draft));
                }}
                onChange={(
                  _event,
                  { value: next }: { value: number | string; direction: string }
                ) => {
                  const parsed = Number(next);
                  setDraft(next === '' || Number.isNaN(parsed) ? null : parsed);
                }}
              />

              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                className={styles.infoNotification}
                title="Estimated lateral length"
                subtitle={`= stick length × factor. The default, ${formatStickLateralFactor(
                  DEFAULT_STICK_LATERAL_FACTOR
                )}, is the median lateral-to-stick ratio of surveyed horizontal wells.`}
              />

              <div className={styles.menuFooter}>
                <Button
                  kind="secondary"
                  onClick={() => setDraft(stickLateralFactorToPercent(value))}
                  disabled={loading || !isModified}
                >
                  Reset
                </Button>
                <Button
                  kind="primary"
                  onClick={handleApply}
                  disabled={loading || !isModified || !isDraftValid}
                >
                  {loading ? 'Applying...' : 'Apply'}
                </Button>
              </div>
            </div>
          </OverflowMenu>
        </div>
      </div>
    </div>
  );
};
